import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';

import { LocalStorage } from 'angular-web-storage';

import { ListResponse } from '../../../../models/list-response';
import {
  canChangePaymentCustomer,
  canRefundPayment,
  getPaymentMethodTypeDisplay,
  getPaymentStatusDisplay,
  getPaymentTypeDisplay,
  getSingleBookingId,
  isPOSPayment,
  loadPaymentFiltersFromGetParams,
  Payment,
  PaymentFilters,
} from '../../../../models/payment';

import { AccountService } from '../../../../services/account/account.service';
import { PaymentService } from '../../../../services/payment/payment.service';
import {
  CallbackResult,
  CallbackResultStatus,
  dateStringFromISO8601,
  getActiveDateRangeFilterText,
  getStaffNameDisplay,
  hasPropNotInList,
  propExists,
  propExistsAndIsNonEmpty,
  SavedViewUrlParamName,
  timeStringFromISO8601,
} from '../../../../utils';
import { RelativeDateRange } from '../../../../models/date-range-filter';
import { OrganizationConfigService } from '../../../../services/organization-config/organization-config.service';
import { PaymentFiltersModalComponent } from './modals/payment-filters-modal/payment-filters-modal.component';
import {
  ApiCollectionExportModalComponent,
} from '../modals/api-collection-export-modal/api-collection-export-modal.component';
import { Association } from '../../../../models/association';
import { AssociationService } from '../../../../services/association/association.service';
import { CustomerPayment } from '../../../../models/customer';
import { RefundPaymentModalComponent } from './modals/refund-payment-modal/refund-payment-modal.component';
import {
  ChangePaymentCustomerModalComponent,
} from './modals/change-payment-customer-modal/change-payment-customer-modal.component';
import {
  PAYMENT_DETAILS_QUERY_PARAM,
  PaymentDetailsModalComponent
} from './modals/payment-details-modal/payment-details-modal.component';
import { DetailsModalDeepLink } from '../../details-modal-deep-link';
import { BOOKING_DETAILS_QUERY_PARAM } from '../events/modals/details-modal-query-params';

import { TaxDisplayFormat } from '../../../../models/organization-config-enums';
import { LocationService } from '../../../../services/location/location.service';
import { CapitanLocation } from '../../../../models/location';
import { PaymentMethodType, PaymentStatus, PaymentType } from '../../../../models/payment-enums';
import * as ReportColumns from '../../../../models/report-columns/payments';
import { columnsOfType } from '../../../../reporting-core/column-types';
import { ReportCellType } from '../../../../reporting-core/cell-format';
import * as ExportColumns from '../../../../models/exports/payments';
import { SavedReportViewService } from '../../../../services/saved-report-view/saved-report-view.service';
import { ReportBaseDirective } from '../../report-base.directive';
import { SavedReportViewReportType } from '../../../../models/saved-report-view';


const EXPORT_PAGE_SIZE = 1000;


/**
 * The component representing the list of payments.
 */
@Component({
  selector: 'app-payments',
  templateUrl: './payments.component.html',
  styleUrls: ['./payments.component.css']
})
export class PaymentsComponent extends ReportBaseDirective<PaymentFilters> implements OnInit, OnDestroy {
  // Parameters required by ReportBaseDirective.
  public filters: PaymentFilters = {};

  protected readonly DefaultColumns = [
    ReportColumns.COLUMN_NAME_DATE,
    ReportColumns.COLUMN_NAME_TIME,
    ReportColumns.COLUMN_NAME_INVOICE_ID,
    ReportColumns.COLUMN_NAME_INVOICE_DESCRIPTION,
    ReportColumns.COLUMN_NAME_PAYMENT_METHOD,
    ReportColumns.COLUMN_NAME_AMOUNT,
    ReportColumns.COLUMN_NAME_LINKED_CUSTOMER,
  ];
  protected readonly ReportColumnNames = ReportColumns.REPORT_COLUMN_NAMES;
  protected readonly ColumnHelpText = {};
  protected readonly ReportColumnHeaderOverrides = ReportColumns.COLUMN_HEADER_OVERRIDES;
  protected readonly SavedViewExcludedProps = [
    'created_at_min',
    'created_at_max',
  ];
  protected readonly ReportType = SavedReportViewReportType.payments;

  protected loadFiltersFromGetParams = loadPaymentFiltersFromGetParams;

  // Parameters specific to this component.
  @LocalStorage() public paymentsPageSize;

  public paymentList: ListResponse<Payment>;

  public associationList: Association[];

  public loadingPayments = true;
  public loadingDownload = false;
  public loadingAssociations = true;

  // The most recent result returned from a modal.
  public modalResult: CallbackResult = null;

  // Make relevant enums and library functions available to the template.
  public readonly CallbackResultStatus = CallbackResultStatus;
  public readonly PaymentType = PaymentType;
  public readonly PaymentStatus = PaymentStatus;
  public readonly isPOSPayment = isPOSPayment;
  public readonly canRefundPayment = canRefundPayment;
  public readonly canChangePaymentCustomer = canChangePaymentCustomer;
  public readonly PaymentMethodType = PaymentMethodType;
  public readonly TaxDisplayFormat = TaxDisplayFormat;
  public readonly ReportColumns = ReportColumns;
  public readonly dateStringFromISO8601 = dateStringFromISO8601;
  public readonly timeStringFromISO8601 = timeStringFromISO8601;
  public readonly getPaymentMethodTypeDisplay = getPaymentMethodTypeDisplay;
  public readonly getStaffNameDisplay = getStaffNameDisplay;
  public readonly getContentItem = ExportColumns.getContentItem;
  public readonly getSingleBookingId = getSingleBookingId;

  public readonly ColumnsWithCustomHTML = columnsOfType(ReportColumns.COLUMN_TYPES, ReportCellType.custom);
  public readonly ObjectIDColumns = columnsOfType(ReportColumns.COLUMN_TYPES, ReportCellType.objectId);
  public readonly CurrencyColumns = columnsOfType(ReportColumns.COLUMN_TYPES, ReportCellType.currency);
  public readonly DateColumns = columnsOfType(ReportColumns.COLUMN_TYPES, ReportCellType.date);

  // The number of payment list requests. Used to ensure that only the most
  // recent request is displayed, in the event of concurrent requests (due to,
  // e.g., filtering).
  private paymentListRequestCount = 0;

  private paymentDetailsDeepLink: DetailsModalDeepLink<PaymentDetailsModalComponent, Payment>;

  constructor(
    public accountService: AccountService,
    public orgConfigService: OrganizationConfigService,
    private paymentService: PaymentService,
    private associationService: AssociationService,
    private locationService: LocationService,
    protected activatedRoute: ActivatedRoute,
    protected router: Router,
    protected savedReportViewService: SavedReportViewService,
    protected modalService: NgbModal,
  ) {
    super(
      activatedRoute,
      router,
      savedReportViewService,
      accountService,
      modalService,
    );

    this.paymentDetailsDeepLink = new DetailsModalDeepLink<PaymentDetailsModalComponent, Payment>({
      router,
      route: activatedRoute,
      modalService,
      queryParam: PAYMENT_DETAILS_QUERY_PARAM,
      component: PaymentDetailsModalComponent,
      // This modal is sized by ng-bootstrap's default, not the helper's 'lg'.
      modalOptions: {},
      configure: (instance, paymentId, payment) => {
        instance.paymentId = paymentId;
        // Absent on a deep-linked open; the modal then loads the payment itself.
        instance.payment = payment ?? null;
        instance.onDownloadReceipt = p => this.downloadReceipt(p);
        instance.onViewRefundOptions = p => this.openRefundPaymentModal(p);
      }
    });
  }

  ngOnInit() {
    if (!this.paymentsPageSize) {
      this.paymentsPageSize = 10;
    }
    // This is needed in order for the pageSize to be updated when the html
    // select is first changed.
    // noinspection SillyAssignmentJS
    this.paymentsPageSize = this.paymentsPageSize;

    super.ngOnInit();

    this.loadAssociations();

    // After report-base has read its own query parameters.
    this.paymentDetailsDeepLink.start();
  }

  ngOnDestroy() {
    this.paymentDetailsDeepLink.stop();
  }

  /**
   * Load the page of payments corresponding to the current page and filter
   * selections.
   */
  public loadReport() {
    this.loadingPayments = true;

    // Increment the request count and save a local copy.
    this.paymentListRequestCount++;
    const thisRequestCount = this.paymentListRequestCount;

    this.paymentService
      .getPayments(
        this.pageNumber,
        this.paymentsPageSize,
        this.filters,
        true
      )
      .then(response => {
        // Only update the payment list if there has not been a more recent
        // request initiated.
        if (thisRequestCount === this.paymentListRequestCount) {
          this.paymentList = response;
        }
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => {
        if (thisRequestCount === this.paymentListRequestCount) {
          this.loadingPayments = false;
        }
      });
  }

  public loadAssociations()  {
    this.loadingAssociations = true;

    this.associationService
      .getAssociations(1, 1000, {})
      .then(response => {
        this.associationList = response.results;
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingAssociations = false; });
  }

  /**
   * Get the number of the last page of results.
   */
  public lastPageNumber() {
    if (!this.paymentList) { return 1; }
    return Math.ceil(this.paymentList.count / this.paymentsPageSize);
  }

  /**
   * Whether a non-zero number of payment results have been loaded.
   */
  public paymentResultsPresent(): boolean {
    return (
      this.paymentList &&
      this.paymentList.results &&
      (this.paymentList.results.length > 0)
    );
  }

  public getActiveFilters(): string[] {
    const activeFilters: string[] = [];

    const pfs = this.filters;

    const dateText = getActiveDateRangeFilterText(
      'Date',
      pfs.created_at_min,
      pfs.created_at_max,
      pfs.created_at_range,
    );
    if (dateText !== null) {
      activeFilters.push(dateText);
    }

    if (propExists(pfs.payment_method_in)) {
      const paymentMethodItems = pfs.payment_method_in.split(',');

      if (paymentMethodItems.length === 1) {
        activeFilters.push(
          'Payment Method is ' + getPaymentTypeDisplay(paymentMethodItems[0] as PaymentType)
        );
      } else if (paymentMethodItems.length > 1) {
        activeFilters.push(
          'Payment Method is one of: ' +
          paymentMethodItems
            .map((paymentMethodItem) => {
              return getPaymentTypeDisplay(paymentMethodItem as PaymentType);
            })
            .join(', ')
        );
      }
    }

    if (propExists(pfs.amount_min) && propExists(pfs.amount_max)) {
      if (pfs.amount_min === pfs.amount_max) {
        activeFilters.push('Amount is exactly ' + pfs.amount_min.toString());
      } else {
        activeFilters.push(
          'Amount is between ' + this.orgConfigService.getCurrencySymbol() + pfs.amount_min.toString() +
          ' and ' + this.orgConfigService.getCurrencySymbol() + pfs.amount_max.toString()
        );
      }
    } else if (propExists(pfs.amount_min)) {
      activeFilters.push('Amount is at least ' + this.orgConfigService.getCurrencySymbol() + pfs.amount_min.toString());
    } else if (pfs.amount_max !== undefined) {
      activeFilters.push('Amount is at most ' + this.orgConfigService.getCurrencySymbol() + pfs.amount_max.toString());
    }

    if (propExists(pfs.status_in)) {
      const statusItems = pfs.status_in.split(',');

      if (statusItems.length === 1) {
        activeFilters.push(
          'Status is ' + getPaymentStatusDisplay(statusItems[0] as PaymentStatus)
        );
      } else if (statusItems.length > 1) {
        activeFilters.push(
          'Status is one of: ' +
          statusItems
            .map((statusItem) => {
              return getPaymentStatusDisplay(statusItem as PaymentStatus);
            })
            .join(', ')
        );
      }
    }

    if (propExists(pfs.discount_association_id)) {
      if (this.loadingAssociations) {
        activeFilters.push(
          'Loading...'
        )
      } else {
        let matchedAssociation: Association = null;
        for (const association of this.associationList) {
          if (association.id === pfs.discount_association_id) {
            matchedAssociation = association;
          }
        }
        if (matchedAssociation !== null) {
          activeFilters.push(
            'Used discount from ' + matchedAssociation.name
          );
        } else {
          activeFilters.push(
            'Used discount from [unknown association ID]'
          )
        }
      }
    }

    if (propExists(pfs.location_id)) {
      if (!this.locationService.getLocations()) {
        activeFilters.push(
          'Loading...'
        );
      } else {
        let matchedLocation: CapitanLocation = null;
        for (const loc of this.locationService.getLocations()) {
          if (loc.id === pfs.location_id) {
            matchedLocation = loc;
          }
        }
        if (matchedLocation !== null) {
          activeFilters.push(
            'Location: ' + matchedLocation.name
          );
        } else {
          activeFilters.push(
            'Location: [unknown location]'
          );
        }
      }
    }

    if (propExists(pfs.does_not_have_linked_customer) && pfs.does_not_have_linked_customer) {
      activeFilters.push('No linked customer');
    }

    return activeFilters;
  }

  /**
   * Open a modal for editing the filters.
   */
  public openEditFiltersModal() {
    if (this.loadingQueriesNeededForFilters()) { return; }

    this.modalResult = null;

    const modalRef = this.modalService.open(PaymentFiltersModalComponent);
    modalRef.componentInstance.inputFilters = this.filters;
    modalRef.componentInstance.associationList = this.associationList;
    modalRef.componentInstance.locationList = this.locationService.getLocations();
    modalRef.componentInstance.savedCallback = (newFilters: PaymentFilters) => {
      this.filters = newFilters;
      this.handleFilterChange();
    };
  }

  /**
   * Open a modal for exporting the payments list, with the currently-selected
   * filters.
   */
  public openExportListModal() {
    this.modalResult = null;

    const modalRef = this.modalService.open(ApiCollectionExportModalComponent);
    modalRef.componentInstance.getPage = (pageNum: number) => {
      return this.paymentService.getPayments(
        pageNum,
        EXPORT_PAGE_SIZE,
        this.filters,
        false
      );
    };
    modalRef.componentInstance.getContentItem = ExportColumns.getContentItem;
    modalRef.componentInstance.totalNumPages = Math.ceil(this.paymentList.count / EXPORT_PAGE_SIZE);
    modalRef.componentInstance.savedCallback = (result: CallbackResult) => {
      this.modalResult = result;
    };
    modalRef.componentInstance.columnNames = ExportColumns.COLUMN_NAMES;
    modalRef.componentInstance.exportNameHumanReadable = 'Payments';
    modalRef.componentInstance.exportFilenamePrefix = 'Payments';
  }

  public downloadReceipt(payment: Payment) {
    this.loadingDownload = true;

    this.successMessage = '';
    this.errorMessage = '';

    this.paymentService
      .downloadReceipt(payment.id, payment.invoice_id)
      .then(() => {
        this.successMessage = 'Receipt downloaded successfully.';
      })
      .catch(reason => {
        if (reason.status === 400) {
          reason.error.text().then(text => { this.errorMessage = text; });
        } else if (reason.status === 403) {
          this.errorMessage = 'You do not have permission to download this receipt.';
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingDownload = false; });
  }

  public openRefundPaymentModal(payment: CustomerPayment) {
    const modalRef = this.modalService.open(RefundPaymentModalComponent);
    modalRef.componentInstance.payment = payment;
    modalRef.componentInstance.successCallback = () => {
      this.loadReport();
      this.successMessage = "Payment refund in progress. The refund may take multiple days to reach the customer's account.";
    };
  }

  public openChangePaymentCustomerModal(payment: Payment) {
    const modalRef = this.modalService.open(ChangePaymentCustomerModalComponent);
    modalRef.componentInstance.paymentId = payment.id;
    modalRef.componentInstance.savedCallback = (successMessage: string) => {
      this.successMessage = successMessage;
      this.loadReport();
    };
  }

  public openPaymentDetailsModal(payment: Payment) {
    this.paymentDetailsDeepLink.open(payment.id, payment);
  }

  public getBookingQueryParams(payment: Payment): { [key: string]: number } | null {
    const bookingId = getSingleBookingId(payment);
    return bookingId === null ? null : { [BOOKING_DETAILS_QUERY_PARAM]: bookingId };
  }

  public loadingQueriesNeededForFilters(): boolean {
    return (
      this.loadingAssociations ||
      !this.locationService.getLocations()
    );
  }

  protected setInitialFilterParams(replaceFilters: boolean) {
    if (replaceFilters) {
      this.filters = {};
    } else if (this.aSavedViewWillAutoLoad()) {
      // On initial load a saved view (from a ?saved_view_id link or the user's
      // default report view) is about to populate the filters. Seeding a
      // default here would both block that auto-load (it counts as a
      // user-applied filter) and collide with the view's own date range, so
      // leave the filters untouched and let the saved view define them.
      return;
    }

    // Default to the last 31 days, but only when no date filter is already
    // present. Using the relative-range key (rather than a fixed min) means a
    // saved view's own date range cleanly overwrites this default.
    if (
      !propExists(this.filters.created_at_min) &&
      !propExists(this.filters.created_at_max) &&
      !propExistsAndIsNonEmpty(this.filters.created_at_range)
    ) {
      this.filters.created_at_range = RelativeDateRange.last31Days;
    }
  }

  /**
   * Whether report-base will auto-load a saved view on initial load — i.e. the
   * current filters are "clean" (nothing the user explicitly set) and there is
   * a view to load (a ?saved_view_id link or the user's default report view).
   * Mirrors the condition in ReportBaseDirective.ngOnInit.
   */
  private aSavedViewWillAutoLoad(): boolean {
    if (hasPropNotInList(this.filters, this.SavedViewExcludedProps)) {
      return false;
    }
    const urlSavedViewId =
      this.activatedRoute.snapshot.queryParamMap.get(SavedViewUrlParamName);
    return (
      propExistsAndIsNonEmpty(urlSavedViewId) ||
      propExists(this.effectiveDefaultReportViewId)
    );
  }
}
